/**
 * Walking-stats helpers — shared by the Tour Player (live pause modal +
 * completion screen) so distance and duration formatting stays consistent.
 *
 * Distance is accumulated from live geolocation samples in TourPlayerPage:
 *   sample = { lat, lng, accuracy_m, ts_ms }
 * Only points with acceptable accuracy (<= 30 m) count, and jumps that look
 * like GPS jitter (< 5 m) or teleport artifacts (> 100 m in one tick) are
 * skipped to keep the total honest.
 */

// Haversine distance in metres between two lat/lng points.
export const haversineMeters = (aLat, aLng, bLat, bLng) => {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};

// Filter constants — tuned for walking pace.
export const WALK_MIN_DELTA_M = 5;        // ignore GPS jitter below this
export const WALK_MAX_DELTA_M = 100;      // ignore teleports (bad fix)
export const WALK_MAX_ACCURACY_M = 30;    // reject wobbly fixes

// Given the previous accepted sample and a new sample, return the delta to
// add to the running total (0 if the new sample should be skipped).
// Both samples: { lat, lng, accuracy_m }
export const acceptedDeltaMeters = (prev, next) => {
  if (!prev || !next) return 0;
  if (next.accuracy_m != null && next.accuracy_m > WALK_MAX_ACCURACY_M) return 0;
  const d = haversineMeters(prev.lat, prev.lng, next.lat, next.lng);
  if (!Number.isFinite(d) || d < WALK_MIN_DELTA_M || d > WALK_MAX_DELTA_M) return 0;
  return d;
};

// Locale-aware unit detection — miles for US, UK, Liberia, Myanmar; km elsewhere.
export const usesMilesLocale = () => {
  try {
    const lang = (typeof navigator !== 'undefined' && navigator.language) || '';
    const langs =
      (typeof navigator !== 'undefined' && navigator.languages) || [];
    const all = [lang, ...langs].map((l) => (l || '').toLowerCase());
    return all.some((l) => /-(us|gb|lr|mm)\b/.test(l));
  } catch (_) {
    return false;
  }
};

// Format a metre value into a compact human-friendly string.
export const formatWalkedDistance = (meters, { imperial } = {}) => {
  if (meters == null || !Number.isFinite(meters) || meters < 0) return '—';
  const useMi = imperial ?? usesMilesLocale();
  if (useMi) {
    const feet = meters * 3.28084;
    if (feet < 528) return `${Math.round(feet)} ft`;
    const miles = meters / 1609.344;
    if (miles < 10) return `${miles.toFixed(2)} mi`;
    return `${miles.toFixed(1)} mi`;
  }
  if (meters < 1000) return `${Math.round(meters)} m`;
  const km = meters / 1000;
  if (km < 10) return `${km.toFixed(2)} km`;
  return `${km.toFixed(1)} km`;
};

// Format a seconds value into h:mm:ss / m:ss.
export const formatWalkedDuration = (seconds) => {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '—';
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => n.toString().padStart(2, '0');
  if (h > 0) return `${h}:${pad(m)}:${pad(sec)}`;
  return `${m}:${pad(sec)}`;
};

// Compact average pace label. Returns empty string when values aren't useful.
export const formatWalkingPace = (meters, seconds, { imperial } = {}) => {
  if (!meters || !seconds || meters < 50 || seconds < 30) return '';
  const useMi = imperial ?? usesMilesLocale();
  const hours = seconds / 3600;
  if (useMi) {
    const mph = meters / 1609.344 / hours;
    return `${mph.toFixed(1)} mph`;
  }
  const kmh = meters / 1000 / hours;
  return `${kmh.toFixed(1)} km/h`;
};
